import { useEffect, useRef, useState } from 'react';
import { Feature, ImageTile, MapBrowserEvent } from 'ol';
import OpenLayersMap from 'ol/Map';
import { ImageWMS, TileWMS, Vector as VectorSource } from 'ol/source';
import { VectorImage } from 'ol/layer';
import { css, useTheme } from '@mui/material';
import WKT from 'ol/format/WKT';
import { Circle, Style, Fill, Stroke } from 'ol/style';
import Button from '@mui/material/Button';
import SelectAllIcon from '@mui/icons-material/SelectAll'; //poligono
import CropDinIcon from '@mui/icons-material/CropDin'; //retangulo
import ModeIcon from '@mui/icons-material/Mode'; //freehand
import { Draw, Interaction } from 'ol/interaction';
import { Type } from 'ol/geom/Geometry';
import TileLayer from 'ol/layer/Tile';
import clsx from 'clsx';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';
import { useTranslation } from 'react-i18next';
import Overlay from 'ol/Overlay.js';
import ReactDOM from 'react-dom/client';
import ImageLayer from 'ol/layer/Image';

// import { UseLayerDashboardStore } from '../stores/useLayerDashboardStore';
import { buildBingMapsLayer, buildMap, buildVectorLayer, fit, intersectFeatures, zoomIn, zoomOut } from '../utils/mapUtils';
import { getLabel } from '../utils/attributesUtil';
import { OverlayFeatureLayerMap } from './OverlayFeatureLayerMap';
import { useAPI } from 'hooks/useAPI';
import { IMiniMap } from 'features/seismic/models/interfaces/IMiniMap';
import { ApiType } from 'models/enums/ApiType';
import { GeopostWMSSource } from 'features/seismic/models/classes/layers/GeopostWMSSource';
import { useSessionStore } from 'session/useSessionStore';
import { axios } from 'configurations/axios';
import { GeopostSingleTileWMSSource } from 'features/seismic/models/classes/layers/GeopostSingleTileWMSSource';
import { useLayerDashboardTabStore } from '../context/LayerDashboardContext';

const PROPERTY_ID = 'geopost-id';
const PROPERTY_LABEL = 'geopost-label';
const PROPERTY_DATA = 'geopost-data';

const selectedStyle = new Style({
    image: new Circle({
        radius: 8,
        fill: new Fill({ color: 'black' }),
        stroke: new Stroke({
            color: [255, 0, 0],
            width: 2
        })
    })
});

const defaultStyle = new Style({
    image: new Circle({
        radius: 7,
        fill: new Fill({ color: 'yellow' }),
        stroke: new Stroke({
            color: [255, 0, 0],
            width: 2
        })
    })
});

export const Map = () => {
    const theme = useTheme();
    //const t = useTranslation(['common', 'layerDashboard']);

    const style = css({
        width: '100%',
        height: '100%',
        position: 'relative',

        '& .ol-zoom': {
            display: 'none'
        },
        '.map': {
            width: '100%',
            height: '100%',
        },
        '.container-button': {
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            background: theme.palette.primary.main,
            width: 40,
            left: '.5em',
            top: 20,
            borderRadius: 5,
            border: '1px solid #777',

            svg: {
                fontSize: 28,
                color: '#fff'
            },
            button: {
                minWidth: 0,
                width: '100%',

                '&.selected': {
                    background: '#777'
                },
                '&:hover': {
                    background: '#ccc'
                }
            },
        },
        '.ol-attribution': {
            display: 'none'
        }
    });

    const mapDom = useRef<HTMLDivElement>(null);
    const map = useRef<OpenLayersMap>();
    const layer = useRef<VectorImage<VectorSource>>();
    const selectionLayer = useRef<VectorImage<VectorSource>>();
    const attributeIdentifierRef = useRef<string>('');
    const featureInfoOverlayRef = useRef<Overlay | null>();
    const attributesLabelRef = useRef<string[]>([]);
    const dictionaryFeaturesById = useRef<{ [key: string]: Feature }>({});
    const dictionaryFeaturesSelectedById = useRef<{ [key: string]: Feature }>({});
    const selectInteraction = useRef<Interaction | null>();
    const [selectionType, setSelectionType] = useState<string>('');
    const allLayers = useRef<ImageLayer<ImageWMS>[]>([]);
    const [ visibleLayers, setVisibleLayers ] = useState([...allLayers.current]);
    const { execute } = useAPI<IMiniMap[]>('/SeismicLayers/GetAllLayers');
    const {tenantConfig, jwtToken} = useSessionStore(state => ({
        tenantConfig: state.tenantConfiguration,
        jwtToken: state.jwtToken
    }));

    const { attributes, data, selectedData, setSelectedData } = useLayerDashboardTabStore(state => ({
        attributes: state.attributes,
        data: state.displayableData,
        selectedData: state.selectedData,
        setSelectedData: state.setSelectedData
    }));

    const getAttributesLabel = () => {
        if (!attributes) {
            return [];
        }
        if (!attributesLabelRef.current || attributesLabelRef.current.length === 0) {
            const attributesLabel = attributes.filter(x => x.IsDisplayField);

            if (attributesLabel.length > 0) {
                attributesLabelRef.current = attributesLabel.map(x => x.Name);
            }
            else {
                if (attributes.length > 0) {
                    attributesLabelRef.current = [attributes[0].Name];
                    console.error('Pegando primeiro atributo como label, já que não tem atribute com DisplayField', attributesLabelRef.current, attributes);
                }
            }
        }

        if (!attributesLabelRef.current || attributesLabelRef.current.length === 0) {
            console.error('Attributo label não encontrado em atributos', attributes);
        }

        return attributesLabelRef.current;
    };

    useEffect(() => {
        const bingLayer = buildBingMapsLayer();

        const vectorLayer = buildVectorLayer();
        vectorLayer.set('type', 'mainLayer');
        const internalSelectionLayer = buildVectorLayer();

        const dom = mapDom.current as HTMLElement;
        const layers = [bingLayer];
        const selectionLa = [bingLayer];
        const currentMap = buildMap(dom, layers);

        map.current = currentMap;
        layer.current = vectorLayer;
        selectionLayer.current = internalSelectionLayer;
        fetchAndPrepareLayers(currentMap, [vectorLayer, internalSelectionLayer]);
    }, []);

    const fetchAndPrepareLayers = async (mapInstance: OpenLayersMap, vectorLayers: VectorImage<VectorSource>[]) => {
        let dataMap = await execute({
            apiType: ApiType.LayersApi,
            params: {
                permalinkToken: localStorage['lastAutoCreatedPermalink'] ? localStorage['lastAutoCreatedPermalink'] : ''
            }
        });
        if (dataMap && allLayers.current) {
            for (let i = 0; i < dataMap?.length; i++) {
                const layerData = dataMap[i];
                const source = new GeopostSingleTileWMSSource({
                    url: dataMap[i].HostUrl.replace('http://', '').replace('https://', '') + '/geoserver/wms',
                    params: { LAYERS: dataMap[i].WorkspaceName + ':' + dataMap[i].LayerName },
                }, jwtToken, tenantConfig?.endpoints.miniMapProxy ?? '');

                let layer = new ImageLayer({
                    source,
                });
                allLayers.current = [...allLayers.current, layer];
                mapInstance.addLayer(layer);
            }
            setVisibleLayers([...allLayers.current]);
        }

        vectorLayers.forEach((dataVectorLayer, index) => {
            mapInstance.addLayer(dataVectorLayer);
        });
    };

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const event = (evt: MapBrowserEvent<any>) => {
            const attributeLabel = getAttributesLabel();

            let hasFeature = false;
            map.current?.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
                if (selectInteraction.current) {
                    return;
                }
                if (layer.get('type') === 'mainLayer') {
                    let label = feature.get(PROPERTY_LABEL);
                    if (!label) {
                        label = getLabel(attributeLabel, feature.get(PROPERTY_DATA));
                    }

                    const domNode = document.createElement('div');
                    const root = ReactDOM.createRoot(domNode);
                    root.render(<OverlayFeatureLayerMap text={label} />);

                    if (featureInfoOverlayRef.current){
                        map.current?.removeOverlay(featureInfoOverlayRef.current);
                        featureInfoOverlayRef.current = null;
                    }

                    const overlay = new Overlay({
                        element: domNode,
                        autoPan: {
                            animation: {
                                duration: 250,
                            },
                        },
                    });

                    overlay.setPosition(evt.coordinate);
                    map.current?.addOverlay(overlay);
                    featureInfoOverlayRef.current = overlay;

                    hasFeature = true;

                    return true;
                }
            });

            if (!hasFeature){
                if (featureInfoOverlayRef.current){
                    map.current?.removeOverlay(featureInfoOverlayRef.current);
                    featureInfoOverlayRef.current = null;
                }
            }
        };

        map.current?.on('pointermove', event);

        return () => {
            map.current?.un('pointermove', event);
        };
    }, [getAttributesLabel]);

    useEffect(() => {
        if (!layer.current || !layer.current.getSource()) {
            return;
        }

        layer.current.getSource()!.clear();

        dictionaryFeaturesById.current = {};

        if (data && data.length > 0) {
            const wkt = new WKT();
            const features: Feature[] = [];
            const attributeIdentifier = getAttributeIdentifier();

            for (const item of data) {
                if (item.geom_wkt) {
                    const feature = wkt.readFeature(item.geom_wkt, { featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' });

                    if (attributeIdentifier && feature && item[attributeIdentifier] && !dictionaryFeaturesById.current[item[attributeIdentifier]]) {
                        feature.set(PROPERTY_ID, item[attributeIdentifier]);
                        feature.set(PROPERTY_DATA, item);

                        dictionaryFeaturesById.current[item[attributeIdentifier]] = feature;
                        feature.setStyle(defaultStyle);
                        features.push(feature);
                    }

                }
            }

            layer.current.getSource()!.addFeatures(features);
            const extent = layer.current?.getSource()?.getExtent() ?? map.current?.getView().calculateExtent(map.current?.getSize());
            if (extent) {
                map.current?.getView().fit(extent, {
                    padding: [250, 250, 250, 250],
                    duration: 2000
                });
            }
        }

    }, [data]);

    useEffect(() => {
        if (!layer.current || !layer.current.getSource()) {
            return;
        }

        for (let featureId in dictionaryFeaturesSelectedById.current) {
            dictionaryFeaturesSelectedById.current[featureId].setStyle(defaultStyle);
        }

        dictionaryFeaturesSelectedById.current = {};

        if (selectedData && selectedData.length > 0) {
            const attributeIdentifier = getAttributeIdentifier();

            if (!attributeIdentifier) {
                return;
            }

            for (let selected of selectedData) {
                const currentId: string = selected[attributeIdentifier];

                if (currentId && dictionaryFeaturesById.current[currentId] && !dictionaryFeaturesSelectedById.current[currentId]) {
                    const feature = dictionaryFeaturesById.current[currentId];
                    feature.setStyle(selectedStyle);
                    dictionaryFeaturesSelectedById.current[currentId] = feature;
                }
            }
        }

        selectionLayer.current?.getSource()?.clear();
    }, [selectedData]);

    const getAttributeIdentifier = () => {
        if (!attributes) {
            return;
        }
        if (!attributeIdentifierRef.current) {
            const attribute = attributes.find(x => x.IsIdentifier);

            if (attribute) {
                attributeIdentifierRef.current = attribute.Name;
            }
        }

        if (!attributeIdentifierRef.current) {
            console.error('Attributo identificador não encontrado em atributos', attributes);
        }

        return attributeIdentifierRef.current;
    };

    const setSelectionFreeHand = function () {
        drawSelectionInteraction('Polygon', true, 'freehand');
    };

    const setSelectionPolygon = function () {
        drawSelectionInteraction('Polygon', false, 'polygon');
    };

    const drawSelectionInteraction = (geometryType: Type, freehand: boolean, type: string) => {
        if (!selectionLayer.current || !selectionLayer.current.getSource()) {
            return;
        }

        removeCurrentSelectInteraction();

        if (type === selectionType) {
            setSelectionType('');
            return;
        }

        const interaction = new Draw({
            source: selectionLayer.current.getSource()!,
            type: geometryType,
            freehand: freehand
        });

        interaction.on('drawend', evt => {
            const geometry = evt.feature.getGeometry();

            if (!layer.current || !layer.current.getSource() || !geometry) {
                return;
            }

            const attributeId = getAttributeIdentifier();

            if (!attributeId) {
                return;
            }

            const source = layer.current.getSource()!;
            const features = source.getFeatures();

            const newFeatures = intersectFeatures(geometry, features);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let newSelectData: any[] = [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const dictionaryDataByid: { [key: string]: any[] } = {};

            for (const dataItem of data) {
                const id = dataItem[attributeId];
                if (id) {
                    if (!dictionaryDataByid[id]) {
                        dictionaryDataByid[id] = [];
                    }

                    dictionaryDataByid[id].push(dataItem);
                }
            }

            for (const feature of newFeatures) {
                const id = feature.get(PROPERTY_ID);

                if (id && dictionaryDataByid[id] && dictionaryDataByid[id].length > 0) {
                    newSelectData = newSelectData.concat(dictionaryDataByid[id]);
                }
            }

            setSelectedData(newSelectData);
        });

        map.current?.addInteraction(interaction);

        selectInteraction.current = interaction;

        setSelectionType(type);
    };

    const removeCurrentSelectInteraction = function () {
        if (selectInteraction.current) {
            map.current?.removeInteraction(selectInteraction.current);
        }

        selectInteraction.current = null;
    };

    return (<div css={style}>
        <div className='map' ref={mapDom}></div>
        <div className='container-button'>
            <Button className='item' onClick={() => zoomIn(map.current)} title='Zoom In'>
                <ZoomInIcon />
            </Button>
            <Button className='item' onClick={() => zoomOut(map.current)} title='Zoom Out'>
                <ZoomOutIcon />
            </Button>
            <Button className='item' onClick={() => fit(layer.current, map.current)} title='Fit to Data'>
                <ZoomInMapIcon />
            </Button>
            <Button className={clsx('item', selectionType === 'freehand' && 'selected')} onClick={setSelectionFreeHand} title='Freehand Selection'>
                <ModeIcon />
            </Button>
            <Button className={clsx('item', selectionType === 'polygon' && 'selected')} onClick={setSelectionPolygon} title='Polygon Selection'>
                <SelectAllIcon />
            </Button>
            {/*<Button className="item">
                <CropDinIcon />
            </Button> */ }
        </div>
    </div>);
};